import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import { MenuItem, TextField } from '@mui/material'
import React from 'react'
import Drawer from '../../../components/Drawer'
import { CloseIcon, TasksIcon } from '../../../template/kartera/icons'
import { DESCRIPCION_MAX, NOMBRE_MAX, useTaskFormDrawerModel } from './task-form-drawer-model'
import type { TaskFormDrawerProps } from './task-form-drawer-model'

export type { TaskFormDrawerProps } from './task-form-drawer-model'

const selectMenu = { MenuProps: { slotProps: { paper: { className: 'v2-select-menu' } } } }

const TaskFormDrawer = (props: TaskFormDrawerProps) => {
    const { open, onClose } = props
    const model = useTaskFormDrawerModel(props)
    const { form, change } = model

    return (
        <Drawer open={open} onClose={onClose} bare width={520}>
            <form onSubmit={model.save} className="flex h-full min-h-0 flex-col" role="dialog" aria-modal="true" aria-labelledby="task-form-drawer-title">
                <div className="flex shrink-0 items-center gap-3.5 border-b border-k-line px-6 py-5">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-k-accent-soft text-k-accent-ink">
                        <TasksIcon size={22} />
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col">
                        <h2 id="task-form-drawer-title" className="m-0 text-[21px] font-extrabold tracking-[-0.02em] text-k-ink">Nueva tarea</h2>
                        <p className="m-0 truncate text-sm text-k-muted">Registra un pendiente y asígnalo a un vendedor</p>
                    </div>
                    <button type="button" aria-label="Cerrar" className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-k-line bg-transparent text-k-ink hover:bg-k-hover" onClick={onClose}>
                        <CloseIcon size={18} />
                    </button>
                </div>

                <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
                    {model.error && (
                        <div className="flex items-center gap-3 rounded-[14px] bg-k-danger-soft px-4 py-3.5 text-sm text-k-danger-ink" role="alert">
                            <ErrorOutlineIcon style={{ fontSize: 20 }} />
                            <p className="m-0">{model.error}</p>
                        </div>
                    )}

                    <TextField
                        className="v2-field" variant="filled" fullWidth required autoFocus
                        label="Nombre"
                        placeholder="Ej. Llamar a Jorge para confirmar renovación"
                        helperText={`${form.nombre.length}/${NOMBRE_MAX}`}
                        value={form.nombre}
                        onChange={(event) => change.nombre(event.target.value)}
                        slotProps={{ htmlInput: { maxLength: NOMBRE_MAX } }}
                    />

                    <div>
                        <p className="m-0 mb-2 text-[13px] font-semibold text-k-muted">
                            Prioridad <span className="text-k-danger">*</span>
                        </p>
                        <div role="radiogroup" aria-label="Prioridad" className="grid grid-cols-3 gap-2.5">
                            {model.priorities.map((option) => (
                                <button
                                    key={option.key}
                                    type="button"
                                    role="radio"
                                    aria-checked={option.selected}
                                    onClick={() => change.prioridad(option.key)}
                                    className={`inline-flex h-12 items-center justify-center gap-2 rounded-xl border text-[15px] transition-colors ${option.selected
                                        ? 'border-k-primary bg-k-accent-soft font-bold text-k-ink'
                                        : 'border-transparent bg-k-field font-semibold text-k-ink-2 hover:bg-k-field-hover'}`}
                                >
                                    <span className={`h-2 w-2 rounded-full ${option.dotClass}`} />
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 items-start gap-5 sm:grid-cols-2">
                        <TextField
                            className="v2-field v2-select" variant="filled" fullWidth select required
                            label="Tipo"
                            value={form.tipo}
                            onChange={(event) => change.tipo(event.target.value)}
                            slotProps={{ select: selectMenu }}
                        >
                            {model.tipos.map((item) => <MenuItem key={item.key} value={item.key}>{item.value}</MenuItem>)}
                        </TextField>
                        <TextField
                            className="v2-field v2-select" variant="filled" fullWidth select
                            label="Vendedor"
                            value={form.vendedor}
                            onChange={(event) => change.vendedor(event.target.value)}
                            slotProps={{ select: { displayEmpty: true, ...selectMenu }, inputLabel: { shrink: true } }}
                        >
                            <MenuItem value=""><em>Sin asignar</em></MenuItem>
                            {model.sellers.map((seller) => <MenuItem key={seller.id} value={String(seller.id)}>{seller.label}</MenuItem>)}
                        </TextField>
                    </div>

                    <TextField
                        className="v2-field" variant="filled" fullWidth
                        type="date"
                        label="Vencimiento"
                        helperText="Déjalo vacío si la tarea no tiene fecha límite."
                        value={form.vencimiento}
                        onChange={(event) => change.vencimiento(event.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                    />

                    <TextField
                        className="v2-field" variant="filled" fullWidth required multiline minRows={3}
                        label="Descripción"
                        placeholder="Detalles, contexto o acuerdos"
                        helperText={`${form.descripcion.length}/${DESCRIPCION_MAX}`}
                        value={form.descripcion}
                        onChange={(event) => change.descripcion(event.target.value)}
                        slotProps={{ htmlInput: { maxLength: DESCRIPCION_MAX } }}
                    />
                </div>

                <div className="grid shrink-0 grid-cols-[1fr_1.6fr] gap-3 border-t border-k-line px-6 py-4">
                    <button
                        type="button"
                        className="inline-flex h-12 items-center justify-center rounded-xl border border-k-line bg-transparent text-[15px] font-bold text-k-ink hover:bg-k-hover"
                        onClick={model.cancel}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={model.saving || !model.canSave}
                        className="inline-flex h-12 items-center justify-center rounded-xl bg-k-primary text-[15px] font-bold !text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {model.saving ? 'Guardando…' : 'Crear tarea'}
                    </button>
                </div>
            </form>
        </Drawer>
    )
}

export default TaskFormDrawer
